var __defProp = Object.defineProperty;
var __getOwnPropDesc = Object.getOwnPropertyDescriptor;
var __decorateClass = (decorators, target, key, kind) => {
  var result = kind > 1 ? void 0 : kind ? __getOwnPropDesc(target, key) : target;
  for (var i = decorators.length - 1, decorator; i >= 0; i--)
    if (decorator = decorators[i])
      result = (kind ? decorator(target, key, result) : decorator(result)) || result;
  if (kind && result)
    __defProp(target, key, result);
  return result;
};

// x-basic-date-picker.ts
import { css } from "@adornis/chemistry/directives/css.js";
import "@adornis/chemistry/elements/components/x-button.js";
import "@adornis/chemistry/elements/components/x-flex.js";
import "@adornis/chemistry/elements/components/x-grid.js";
import "@adornis/chemistry/elements/components/x-icon.js";
import { DesignSystem } from "@adornis/chemistry/elements/theming/design.js";
import { xComponents } from "@adornis/chemistry/elements/x-components.js";
import "@adornis/popover/x-dropdown-selection.js";
import { html } from "lit";
import { customElement, state } from "lit/decorators.js";
import { createRef, ref } from "lit/directives/ref.js";
import { repeat } from "lit/directives/repeat.js";
import { DateTime } from "luxon";
import { XDatePicker } from "./x-date-picker.js";
var XBasicDatePicker = class extends XDatePicker {
  constructor() {
    super(...arguments);
    this._showPending = false;
    this._grid = createRef();
  }
  styles() {
    return [
      {
        [`${DesignSystem.prefix}-grid`]: {
          userSelect: "none",
          textAlign: "center",
          [`${DesignSystem.prefix}-flex`]: { border: `1px solid transparent` },
          "[blocked]": {
            background: this.colors.neutral.shade(50),
            color: this.colors.dark,
            pointerEvents: "none"
          },
          "[out-of-month]": {
            background: this.colors.neutral.shade(50),
            color: this.colors.dark
          },
          "[selected]": { background: this.colors.neutral.shade(100) },
          "[pending]": { border: `1px solid ${this.colors.neutral.shade(500)}` },
          "[highlighted]": { background: this.colors.neutral.shade(300) },
          "& > *:hover": { background: this.colors.neutral.shade(100) }
        }
      }
    ];
  }
  render() {
    return xComponents(html`
      <x-flex vertical space="md">
        ${this.header()}

        <x-grid
          columns="7"
          tabindex="0"
          ${ref(this._grid)}
          @focus=${() => this._showPending = true}
          @blur=${() => this._showPending = false}
          @keydown=${(e) => this.handleKeyDown(e)}
          ${css({ width: "100%", pointerEvents: this.disabled ? "none" : "all" })}
        >
          <div ${css({ display: "contents", fontWeight: "bold" })}>${this.weekline()}</div>

          ${this.numbers()}
        </x-grid>
      </x-flex>
    `);
  }
  focus() {
    this._grid.value?.focus();
  }
  weekline() {
    return html`
      ${this.weeklineData.map(
      (day) => html` <x-flex padding="sm" weekday crossaxis-center center> ${this.weeklineEntry(day)} </x-flex>`
    )}
    `;
  }
  numbers() {
    return html`
      ${repeat(
      this.getDateGridForMonth(),
      (date) => date.toLocaleString(DateTime.DATETIME_SHORT),
      (date) => {
        const selected = this.value.value?.day === date.day && this.value.value?.month === date.month && this.value.value?.year === date.year;
        const pendingSelection = this._showPending && this.date.value?.day === date.day && this.date.value?.month === date.month && this.date.value?.year === date.year;
        return html`
            <x-flex
              padding="sm"
              monthnumber
              crossaxis-center
              center
              ${css({ cursor: "pointer" })}
              ?selected=${selected}
              ?pending=${pendingSelection}
              ?out-of-month=${date.month !== this.date.value?.month}
              ?blocked=${this.isDateBlocked(date)}
              ?highlighted=${this.highlightedDate(date)}
              @click=${() => {
          if (this.disabled)
            return;
          this.date.next(date);
          this.value.next(date);
          this.dispatchEvent(new CustomEvent("value-picked", { detail: { value: date, clickedDay: true } }));
        }}
            >
              ${this.number(date.day)}
            </x-flex>
          `;
      }
    )}
    `;
  }
  number(day) {
    return html` <x-text> ${day} </x-text> `;
  }
  weeklineEntry(day) {
    return html` <x-text> ${day} </x-text> `;
  }
  header() {
    return html`
      <x-flex horizontal crossaxis-center space-between space="xs">
        <x-flex horizontal flex crossaxis-center>
          <x-button
            mode="icon"
            icon="chevron_left"
            tabindex="-1"
            @click=${() => {
      this.date.minus({ months: 1 });
    }}
          ></x-button>
          <x-dropdown-selection
            flex
            placeholder="Monat"
            placeholder-mode="hide"
            .selectables=${Array(12).fill(0).map((_, i) => i + 1)}
            .renderString=${(num) => DateTime.now().set({ month: num }).monthLong}
            @value-changed=${(e) => {
      if (e.detail.value && e.detail.value !== this.date.value.month) {
        this.date.set({ month: e.detail.value });
      }
    }}
            .value=${this.date.value.month}
          ></x-dropdown-selection>
          <x-button
            mode="icon"
            icon="chevron_right"
            tabindex="-1"
            @click=${() => {
      this.date.plus({ months: 1 });
    }}
          ></x-button>
        </x-flex>
        <x-dropdown-selection
          placeholder="Jahr"
          placeholder-mode="hide"
          .selectables=${[...Array(this["end-year"] - this["start-year"]).keys()].map((y) => y + this["start-year"]).reverse()}
          @value-changed=${(e) => {
      if (e.detail.value && e.detail.value !== this.date.value.year) {
        this.date.set({ year: e.detail.value });
      }
    }}
          .value=${this.date.value.year}
        ></x-dropdown-selection>
      </x-flex>
    `;
  }
};
__decorateClass([
  state()
], XBasicDatePicker.prototype, "_showPending", 2);
XBasicDatePicker = __decorateClass([
  customElement("x-basic-date-picker")
], XBasicDatePicker);
export {
  XBasicDatePicker
};
